/**
 * ConnectCallbackResult (Phase 2, §7.5) — the landing page the EB callback
 * 303-redirects to. One component serves both routes:
 *
 *  - `/verbindung/erfolg?connection={id}&bootstrap=pending`: invalidate the
 *    `['connections']`, `['accounts']`, `['transactions']` and `['inbox']`
 *    caches, and — when `bootstrap=pending` — fire the attended full-history
 *    bootstrap sync (`trigger:'bootstrap'`) with a spinner, then show the
 *    discovered accounts.
 *  - `/verbindung/fehler?grund={unbekannt|abgebrochen|fehler}`: map the reason
 *    to a German message and offer a retry.
 *
 * The success flow never leaks secrets — only the opaque connection id and the
 * `bootstrap` flag ride in the query string.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { Spinner } from '../../components/Spinner.tsx';
import { ErrorState } from '../../components/ui/ErrorState.tsx';
import { SkeletonList } from '../../components/ui/Skeleton.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { qk } from '../../lib/queryKeys.ts';
import { useConnection } from '../../lib/hooks/useConnections.ts';
import { useSyncConnection } from '../../lib/hooks/useConnectionMutations.ts';
import { ConnectionCard } from './components/ConnectionCard.tsx';

const SUCCESS_PATH = '/verbindung/erfolg';

const GRUND_TEXT: Record<string, string> = {
  unbekannt: 'Verknüpfung abgelaufen oder unbekannt',
  abgebrochen: 'SCA abgebrochen',
  fehler: 'Verbindung fehlgeschlagen',
};

export default function ConnectCallbackResult() {
  const location = useLocation();
  const isSuccess = location.pathname === SUCCESS_PATH;
  return isSuccess ? <CallbackSuccess /> : <CallbackFailure />;
}

function CallbackSuccess() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const sync = useSyncConnection();
  const didRunRef = useRef(false);

  const connectionId = Number(params.get('connection'));
  const bootstrapPending = params.get('bootstrap') === 'pending';
  const validId = Number.isFinite(connectionId) && connectionId > 0;

  const connectionQuery = useConnection(validId ? connectionId : NaN);

  useEffect(() => {
    if (didRunRef.current) return;
    didRunRef.current = true;
    // Fresh consent may have created accounts and (via bootstrap) transactions.
    void qc.invalidateQueries({ queryKey: qk.connections });
    void qc.invalidateQueries({ queryKey: qk.accounts });
    void qc.invalidateQueries({ queryKey: qk.transactions });
    void qc.invalidateQueries({ queryKey: qk.inbox });
    if (bootstrapPending && validId) {
      sync.mutate({ id: connectionId, body: { trigger: 'bootstrap' } });
    }
    // Run exactly once on mount — the ref guards against StrictMode double-invoke.
    // eslint-disable-next-line react-hooks/exhaustive-deps, @eslint-react/exhaustive-deps
  }, []);

  return (
    <div>
      <PageHeader title="Bank verbunden" />
      <div className="w-full space-y-6 px-4 py-6 sm:px-6">
        <div className="flex items-center gap-3 rounded-lg border-l-4 border-emerald-500 bg-emerald-50 p-4 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200">
          <CheckCircle2 className="h-6 w-6 shrink-0" />
          <div>
            <p className="font-medium">Zustimmung erteilt</p>
            <p className="text-sm opacity-90">Deine Bank wurde erfolgreich verbunden.</p>
          </div>
        </div>

        {/* Failed bootstrap sync must never be silent: the consent DID succeed
            (green banner stays), but the attended full-history import needs a
            visible warning + a retry that re-sends trigger:'bootstrap' so the
            full 730-day window is preserved (a later manual sync would only
            fetch the short unattended window). */}
        {sync.isError && !sync.isPending ? (
          <div className="flex items-start gap-3 rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200">
            <AlertTriangle className="h-6 w-6 shrink-0" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="font-medium">Umsatz-Import fehlgeschlagen</p>
              <p className="text-sm opacity-90">
                Die Verbindung wurde hergestellt, aber die Umsätze konnten nicht geladen werden.
                {sync.error instanceof Error && sync.error.message
                  ? ` (${sync.error.message})`
                  : ''}
              </p>
            </div>
            <button
              type="button"
              onClick={() => sync.mutate({ id: connectionId, body: { trigger: 'bootstrap' } })}
              className="shrink-0 text-sm font-semibold underline underline-offset-2 transition-colors duration-150 hover:opacity-80"
            >
              Erneut versuchen
            </button>
          </div>
        ) : null}

        {sync.isPending ? (
          <div className="py-10">
            <Spinner label="Umsätze werden geladen …" />
          </div>
        ) : !validId ? (
          <ErrorState
            title="Keine Verbindung angegeben"
            message="Die Rückmeldung enthielt keine gültige Verbindung."
          />
        ) : connectionQuery.isPending ? (
          <SkeletonList rows={2} />
        ) : connectionQuery.isError ? (
          <ErrorState
            error={connectionQuery.error}
            onRetry={() => void connectionQuery.refetch()}
          />
        ) : (
          <ConnectionCard connection={connectionQuery.data} linkToDetail={false} />
        )}

        <div className="flex justify-end">
          <Button onClick={() => void navigate('/bankverbindungen')}>
            Zu den Bankverbindungen
          </Button>
        </div>
      </div>
    </div>
  );
}

function CallbackFailure() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const grund = params.get('grund') ?? 'fehler';
  const message = GRUND_TEXT[grund] ?? GRUND_TEXT.fehler;

  return (
    <div>
      <PageHeader title="Verbindung fehlgeschlagen" />
      <div className="w-full space-y-6 px-4 py-6 sm:px-6">
        <div className="flex items-center gap-3 rounded-lg border-l-4 border-rose-500 bg-rose-50 p-4 text-rose-800 dark:bg-rose-950/50 dark:text-rose-200">
          <XCircle className="h-6 w-6 shrink-0" />
          <div>
            <p className="font-medium">{message}</p>
            <p className="text-sm opacity-90">
              Die Bankverbindung konnte nicht hergestellt werden.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => void navigate('/bankverbindungen')}>
            Zurück
          </Button>
          <Button onClick={() => void navigate('/bankverbindungen/einrichten')}>
            Erneut versuchen
          </Button>
        </div>
      </div>
    </div>
  );
}
